import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, type AppInfo } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import { ClockIcon, GlobeIcon, PlusIcon, GridIcon } from '../components/icons';
import { firstChar, iconGradientClass, relativeTime } from '../lib/utils';

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: apps, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['apps'],
    queryFn: api.getApps,
  });

  return (
    <div className="page-wrap-wide">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            我的应用
            {apps && apps.length > 0 && (
              <span className="plan-badge">{apps.length} 个应用</span>
            )}
          </h1>
          <p className="page-subtitle">管理你的 Cloupak 应用与构建</p>
        </div>
        <button className="btn btn-accent" onClick={() => navigate('/create')}>
          <PlusIcon size={14} />
          创建新应用
        </button>
      </div>

      {isLoading && <div className="state-box">加载中…</div>}

      {error && (
        <div className="state-box state-error">
          <span>加载失败：{(error as Error).message}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => refetch()}>
            重试
          </button>
        </div>
      )}

      {apps && apps.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">
            <GridIcon size={32} />
          </div>
          <h2>还没有应用</h2>
          <p>创建你的第一个应用，上传 ZIP 包即可开始构建。</p>
          <button className="btn btn-accent" onClick={() => navigate('/create')}>
            创建第一个应用
          </button>
        </div>
      )}

      {apps && apps.length > 0 && (
        <>
          {isFetching && !isLoading && <div className="refresh-hint">刷新中…</div>}
          <div className="app-grid">
            {apps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AppCard({ app }: { app: AppInfo }) {
  const accessUrl = app.status === 'success' ? `https://${app.id}.apps.cloupak.app` : null;
  return (
    <Link to={`/apps/${app.id}`} className="app-card">
      <div className="app-card-header">
        <div className={`app-icon ${iconGradientClass(app.name)}`}>
          {firstChar(app.name)}
        </div>
        <div className="app-info">
          <div className="app-name">{app.name}</div>
          <div className="app-repo" title={app.id}>{app.id}</div>
        </div>
      </div>

      <div className="app-card-status">
        <StatusBadge status={app.status} />
      </div>

      <div className="app-meta">
        <span className="meta-item">
          <ClockIcon size={12} />
          {relativeTime(app.lastBuildAt ?? app.createdAt)}
        </span>
      </div>

      {accessUrl && (
        <div className="app-link">
          <GlobeIcon size={12} />
          {app.id}.apps.cloupak.app
        </div>
      )}
    </Link>
  );
}
